import { useEffect, useRef, useState } from 'react';
import { Logo } from '../components/Logo';

// Public /badge page: a pilot fills this, and /api/badge-app fills the exact LGB
// Airport Security ID Media Application (Form 1002, AOA) and emails it to them.
const DRAFT_KEY = 'ajm-badge-draft-v1';
const ENDPOINT = `${import.meta.env.BASE_URL}api/badge-app`;

// LGB Form 1010: one List A document, or one List B + one List C. Naturalized /
// born-abroad citizens also need a citizenship document (passport, naturalization
// or birth-abroad certificate).
const LIST_A = ['passport', 'permRes', 'ead'];
const LIST_B = ['license'];
const LIST_C = ['ssCard', 'birthCert', 'birthAbroad'];
const CITIZENSHIP_DOCS = ['passport', 'naturalization', 'birthAbroad'];
const ID_DOCS: { key: string; label: string }[] = [
  { key: 'passport', label: 'Passport / Passport Card' },
  { key: 'license', label: "Driver's License / State ID" },
  { key: 'birthCert', label: 'Birth Certificate' },
  { key: 'ssCard', label: 'Social Security Card' },
  { key: 'permRes', label: 'Permanent Resident Card' },
  { key: 'ead', label: 'Employment Authorization Doc' },
  { key: 'birthAbroad', label: 'Certificate of Birth Abroad' },
  { key: 'naturalization', label: 'Certificate of Naturalization' },
  { key: 'other', label: 'Other' },
];
const ETHNICITY = [
  { key: 'asian', label: 'Asian / Pacific Islander' },
  { key: 'white', label: 'White' },
  { key: 'hispanic', label: 'Hispanic' },
  { key: 'black', label: 'Black' },
  { key: 'other', label: 'Other' },
];

type Alias = { last: string; first: string; middle: string };
const BLANK = {
  firstName: '', middleName: '', lastName: '',
  phone: '', email: '',
  address: '', apt: '', city: '', state: '', zip: '',
  heightFt: '', heightIn: '', weight: '', eyeColor: '', hairColor: '', gender: '', ethnicity: '',
  birthCountry: '', birthState: '', usCitizen: '', dob: '',
  idOther: '', reason: 'new',
};

function loadDraft(): { f?: typeof BLANK; aliases?: Alias[]; idDocs?: string[] } | null {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
}

export function BadgeApplication() {
  const usDate = (iso: string) => (iso ? iso.slice(5, 7) + '/' + iso.slice(8, 10) + '/' + iso.slice(0, 4) : '');
  const draft = useRef(loadDraft()).current;
  const [f, setF] = useState(() => ({ ...BLANK, ...(draft?.f || {}) }));
  const [aliases, setAliases] = useState<Alias[]>(() => draft?.aliases ?? []);
  const [idDocs, setIdDocs] = useState<string[]>(() => draft?.idDocs ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [honey, setHoney] = useState('');
  const openedAt = useRef(Date.now()).current;

  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ f, aliases, idDocs }));
    } catch {
      /* storage full / disabled — non-fatal */
    }
  }, [f, aliases, idDocs]);

  const upd = (k: keyof typeof BLANK, v: string) => setF((p) => ({ ...p, [k]: v }));
  const toggleId = (k: string) => setIdDocs((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const updAlias = (i: number, k: keyof Alias, v: string) => setAliases((p) => p.map((a, ai) => (ai === i ? { ...a, [k]: v } : a)));

  const blank = (v: string) => !v.trim();
  const has = (list: string[]) => idDocs.some((k) => list.includes(k));
  const bornAbroad = !!f.birthCountry.trim() && !/^(usa?|united states( of america)?|u\.s\.a?\.?)$/i.test(f.birthCountry.trim());
  const idPairOk = idDocs.includes('other') || has(LIST_A) || (has(LIST_B) && has(LIST_C));
  const missing = [
    blank(f.firstName) && 'first name',
    blank(f.lastName) && 'last name',
    blank(f.email) && 'email',
    blank(f.phone) && 'phone',
    blank(f.address) && 'street address',
    blank(f.city) && 'city',
    blank(f.state) && 'state',
    blank(f.zip) && 'ZIP',
    !f.dob && 'date of birth',
    blank(f.birthCountry) && 'country of birth',
    blank(f.birthState) && 'state / province of birth',
    !f.usCitizen && 'U.S. citizen',
    blank(f.heightFt) && 'height',
    blank(f.weight) && 'weight',
    blank(f.gender) && 'gender',
    blank(f.eyeColor) && 'eye color',
    blank(f.hairColor) && 'hair color',
    !f.ethnicity && 'ethnicity',
    idDocs.length < 2 && '2 forms of ID',
    idDocs.length >= 2 && !idPairOk && 'an ID pair LGB accepts (a passport, green card or work permit, OR a license/state ID plus a Social Security card or birth certificate)',
    idDocs.includes('other') && blank(f.idOther) && 'a description of your "Other" ID',
    f.usCitizen === 'yes' && bornAbroad && !has(CITIZENSHIP_DOCS) && 'a U.S. passport, naturalization certificate or birth-abroad certificate (you were born outside the U.S.)',
  ].filter(Boolean) as string[];

  async function submit() {
    setErr(null);
    if (missing.length) {
      setErr(`Still need: ${missing.join(', ')}.`);
      return;
    }
    setBusy(true);
    const data = {
      firstName: f.firstName, middleName: f.middleName, lastName: f.lastName,
      aliases: aliases.filter((a) => a.last.trim() || a.first.trim()),
      phone: f.phone, email: f.email.trim(),
      address: f.address, apt: f.apt, city: f.city, state: f.state, zip: f.zip,
      height: f.heightFt ? `${f.heightFt} ft. ${f.heightIn || 0} in.` : '',
      weight: f.weight ? `${f.weight} lbs` : '',
      eyeColor: f.eyeColor, hairColor: f.hairColor, gender: f.gender, ethnicity: f.ethnicity,
      birthCountry: f.birthCountry, birthState: f.birthState, usCitizen: f.usCitizen, dob: usDate(f.dob),
      idDocs, idOther: idDocs.includes('other') ? f.idOther : '',
      reason: f.reason,
    };
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, website: honey, elapsedMs: Date.now() - openedAt }),
      });
      const j = await res.json().catch(() => ({ ok: false, message: 'Something went wrong. Please try again.' }));
      if (!j.ok) throw new Error(j.message || 'Something went wrong.');
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-sky-50 px-5 py-16">
        <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-3xl">✓</div>
          <h1 className="font-display text-2xl font-bold text-navy-900">Check your email.</h1>
          <p className="mt-3 text-sm text-ink-muted">
            Thanks, {f.firstName || 'there'}. Your filled KLGB badge application is on its way to <strong>{f.email}</strong>, with
            the steps to finish: sign page 2 in ink, get your signatory to sign page 4, then turn it in.
          </p>
          <p className="mt-4 text-xs text-ink-subtle">Don't see it? Check spam. You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50">
      <div className="border-b border-slate-200 bg-white px-5 pb-6 pt-7 md:px-0 md:pb-8 md:pt-10">
        <div className="mx-auto max-w-md md:max-w-3xl md:px-8 md:flex md:items-end md:justify-between md:gap-6">
          <div>
            <Logo className="text-3xl md:text-4xl" />
            <h1 className="mt-5 font-display text-2xl font-bold leading-tight text-navy-900 md:mt-7 md:text-4xl">KLGB Badge Application</h1>
            <p className="mt-0.5 text-sm font-semibold text-navy-700 md:mt-1 md:text-base">Long Beach Airport · AOA badge</p>
          </div>
          <p className="mt-2 text-xs text-ink-subtle md:mb-1 md:mt-0 md:text-sm">Progress saves automatically</p>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-5 px-5 py-6 md:max-w-3xl md:space-y-6 md:px-8 md:py-10">
        <p className="text-sm text-ink-muted">
          Fill this in once. We'll fill out the official LGB form for you and email you the PDF, ready to sign. Use your name
          exactly as it appears on your IDs.
        </p>

        <Section title="Your name">
          <Grid cols={3}>
            <Field label="First" value={f.firstName} onChange={(v) => upd('firstName', v)} required />
            <Field label="Middle" value={f.middleName} onChange={(v) => upd('middleName', v)} />
            <Field label="Last" value={f.lastName} onChange={(v) => upd('lastName', v)} required wide />
          </Grid>
          <div className="pt-1">
            <div className="text-sm font-medium text-navy-900">Other names you've used</div>
            <p className="text-xs text-ink-muted">
              Every name you've used: maiden or former married names, nicknames on records, and any part of a relative's name you've
              gone by (a parent's or grandparent's last name, a foster or adopted last name). LGB can turn you down if one is missing.
            </p>
          </div>
          {aliases.map((a, i) => (
            <div key={i}>
              <Grid cols={3}>
                <Field label="First" value={a.first} onChange={(v) => updAlias(i, 'first', v)} />
                <Field label="Middle" value={a.middle} onChange={(v) => updAlias(i, 'middle', v)} />
                <Field label="Last" value={a.last} onChange={(v) => updAlias(i, 'last', v)} wide />
              </Grid>
              <button type="button" className="mt-1 text-xs text-ink-subtle underline" onClick={() => setAliases((p) => p.filter((_, ai) => ai !== i))}>
                Remove
              </button>
            </div>
          ))}
          {aliases.length < 3 && (
            <button type="button" className="text-xs font-medium text-navy-700 underline" onClick={() => setAliases((p) => [...p, { last: '', first: '', middle: '' }])}>
              + Add another name
            </button>
          )}
        </Section>

        <Section title="Contact & address" hint="Mailing address must be in the U.S.">
          <Grid cols={2}>
            <Field label="Phone" type="tel" value={f.phone} onChange={(v) => upd('phone', v)} required />
            <Field label="Email" type="email" value={f.email} onChange={(v) => upd('email', v)} required wide />
          </Grid>
          <Grid cols={2}>
            <Field label="Street address" value={f.address} onChange={(v) => upd('address', v)} required wide />
            <Field label="Apt / Suite" value={f.apt} onChange={(v) => upd('apt', v)} />
          </Grid>
          <Grid cols={3}>
            <Field label="City" value={f.city} onChange={(v) => upd('city', v)} required wide />
            <Field label="State" value={f.state} onChange={(v) => upd('state', v)} required />
            <Field label="ZIP" value={f.zip} onChange={(v) => upd('zip', v)} required />
          </Grid>
        </Section>

        <Section title="About you">
          <Grid cols={3}>
            <Field label="Date of birth" type="date" value={f.dob} onChange={(v) => upd('dob', v)} required />
            <Field label="Country of birth" value={f.birthCountry} onChange={(v) => upd('birthCountry', v)} required />
            <Field label="State / province of birth" value={f.birthState} onChange={(v) => upd('birthState', v)} required wide />
          </Grid>
          <YesNo label="Are you a U.S. citizen?" value={f.usCitizen} onChange={(v) => upd('usCitizen', v)} />
          <Grid cols={4}>
            <Field label="Height (ft)" type="number" value={f.heightFt} onChange={(v) => upd('heightFt', v)} required />
            <Field label="Height (in)" type="number" value={f.heightIn} onChange={(v) => upd('heightIn', v)} />
            <Field label="Weight (lbs)" type="number" value={f.weight} onChange={(v) => upd('weight', v)} required />
            <Field label="Gender" value={f.gender} onChange={(v) => upd('gender', v)} required />
            <Field label="Eye color" value={f.eyeColor} onChange={(v) => upd('eyeColor', v)} required />
            <Field label="Hair color" value={f.hairColor} onChange={(v) => upd('hairColor', v)} required />
          </Grid>
          <div>
            <label className="label">Ethnicity<span className="text-red-500"> *</span></label>
            <Chips options={ETHNICITY} selected={f.ethnicity ? [f.ethnicity] : []} onToggle={(k) => upd('ethnicity', f.ethnicity === k ? '' : k)} />
          </div>
        </Section>

        <Section
          title="Your 2 IDs"
          hint="Pick the two original IDs you'll bring every time (no copies, unexpired, not laminated). Names must match exactly, and one needs a photo."
        >
          <Chips options={ID_DOCS} selected={idDocs} onToggle={toggleId} />
          <p className="text-xs text-ink-muted">
            LGB takes a passport, green card or work permit, OR a driver's license / state ID plus a Social Security card or birth
            certificate. Born outside the U.S. and a citizen? You also need a U.S. passport, naturalization certificate or
            birth-abroad certificate.
          </p>
          {idDocs.includes('other') && <Field label="Other ID (describe)" value={f.idOther} onChange={(v) => upd('idOther', v)} required />}
          <a className="inline-block text-xs font-medium text-navy-700 underline" href="/lgb-acceptable-ids.pdf" target="_blank" rel="noreferrer">
            LGB's full list of accepted IDs
          </a>
        </Section>

        <Section title="Send it">
          <div>
            <label className="label">This is a</label>
            <Chips
              options={[{ key: 'new', label: 'New badge' }, { key: 'renewal', label: 'Renewal' }]}
              selected={[f.reason]}
              onToggle={(k) => upd('reason', k)}
            />
          </div>
          {f.reason === 'renewal' && (
            <p className="text-xs text-ink-muted">Renewing? Your current badge must still be valid. If it has expired, choose New badge.</p>
          )}
          <p className="text-xs text-ink-subtle">
            We'll email the filled application to you, with a copy to AJM. We don't keep your info on our servers; your draft stays
            in this browser until you send it. AOA badges don't need your SSN.
          </p>
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
            value={honey}
            onChange={(e) => setHoney(e.target.value)}
          />
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="button" onClick={submit} disabled={busy} className="btn-primary w-full py-3 disabled:opacity-50 md:w-auto md:px-10">
            {busy ? 'Sending…' : 'Email me my application'}
          </button>
        </Section>

        <p className="pb-10 text-center text-xs text-ink-subtle">AJM Aviation Ventures, LLC · Long Beach (KLGB)</p>
      </div>
    </div>
  );
}

/* ---- little building blocks (same look as the Named Pilot Form) ---- */
function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm md:p-7">
      <h2 className="font-display text-lg font-semibold text-navy-900 md:text-xl">{title}</h2>
      {hint && <p className="mb-3 mt-0.5 text-xs text-ink-muted">{hint}</p>}
      <div className={hint ? 'space-y-3' : 'mt-3 space-y-3'}>{children}</div>
    </div>
  );
}
const GRID_COLS = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' } as const;
function Grid({ cols, children }: { cols: keyof typeof GRID_COLS; children: React.ReactNode }) {
  return <div className={`grid grid-cols-2 gap-3 md:gap-4 ${GRID_COLS[cols]}`}>{children}</div>;
}
function Field({ label, value, onChange, type = 'text', required, wide }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2 md:col-span-1' : undefined}>
      <label className="label">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input className="input" type={type} inputMode={type === 'number' ? 'numeric' : undefined} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Chips({ options, selected, onToggle }: { options: { key: string; label: string }[]; selected: string[]; onToggle: (k: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onToggle(o.key)}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
            selected.includes(o.key) ? 'bg-brand text-white' : 'bg-white text-navy-800 ring-1 ring-inset ring-slate-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
function YesNo({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label">{label}<span className="text-red-500"> *</span></label>
      <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-inset ring-slate-300">
        {(['yes', 'no'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => onChange(a)}
            className={`px-5 py-2 text-sm font-medium capitalize transition ${value === a ? 'bg-brand text-white' : 'bg-white text-ink-muted'}`}
          >
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}
